// Responsive table — renders as a real <table> on >=md viewports and as a
// stacked card list on smaller ones. Use for read-mostly tables (PR/PO lists,
// payment requests, item master, etc.) where horizontal scrolling on a phone
// is unacceptable.
//
// Heavy data grids (AG-Grid, MUI DataGrid) are kept as-is; ResponsiveTable
// targets the simpler tables that we hand-render today.
//
// Usage:
//   <ResponsiveTable
//     ariaLabel="Payment Requests"
//     columns={[
//       { id: 'docref', header: 'Document Ref', cell: (r) => r.docref },
//       { id: 'amount', header: 'Amount', cell: (r) => fmt(r.amount), align: 'right' },
//     ]}
//     rows={data}
//     getRowKey={(r) => r.id}
//     onRowClick={(r) => navigate(`/payment-requests/${r.id}`)}
//   />

import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useIsMobile } from '../../hooks/useBreakpoints';

export interface ResponsiveTableColumn<R> {
  id: string;
  header: React.ReactNode;
  cell: (row: R, index: number) => React.ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Hide this column on mobile-card view (e.g. low-importance metadata). */
  hideOnMobile?: boolean;
  /** Width hint for the desktop table (CSS value or number for px). */
  width?: number | string;
}

/**
 * Optional row selection for bulk actions: a checkbox column on the desktop table; on phones a "Select" line above
 * each card (outside its tap area, a checkbox cannot sit inside a button) and a "Select all" line above the list.
 */
export interface ResponsiveTableSelection<R> {
  /** The keys (getRowKey) of the selected rows. */
  selected: ReadonlySet<string | number>;
  onChange: (next: Set<string | number>) => void;
  /** Rows it refuses get no checkbox. */
  isSelectable?: (row: R) => boolean;
  /** Each checkbox's accessible name, e.g. "Select PC-2026-11-0012". */
  label: (row: R) => string;
}

interface ResponsiveTableProps<R> {
  /**
   * Required for screen readers. Use a short name describing what the rows are
   * (e.g. "Payment Requests", "Purchase Order Lines").
   */
  ariaLabel: string;
  caption?: React.ReactNode;
  columns: ResponsiveTableColumn<R>[];
  rows: R[];
  getRowKey: (row: R, index: number) => string | number;
  onRowClick?: (row: R, index: number) => void;
  emptyMessage?: React.ReactNode;
  /** Optional density override for the desktop table. */
  size?: 'small' | 'medium';
  selection?: ResponsiveTableSelection<R>;
}

function ResponsiveTable<R>({
  ariaLabel,
  caption,
  columns,
  rows,
  getRowKey,
  onRowClick,
  emptyMessage = 'No records found',
  size = 'small',
  selection,
}: ResponsiveTableProps<R>) {
  const mobile = useIsMobile();
  const canSelect = (row: R): boolean => !!selection && (!selection.isSelectable || selection.isSelectable(row));
  const selectableKeys = selection ? rows.flatMap((row, i) => (canSelect(row) ? [getRowKey(row, i)] : [])) : [];
  const selectedCount = selection ? selectableKeys.filter((k) => selection.selected.has(k)).length : 0;
  const allSelected = selectableKeys.length > 0 && selectedCount === selectableKeys.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const toggleAll = () => selection?.onChange(allSelected ? new Set() : new Set(selectableKeys));
  const toggle = (key: string | number) => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    selection.onChange(next);
  };

  if (rows.length === 0) {
    return (
      <Box
        role="status"
        sx={{
          py: 4,
          textAlign: 'center',
          color: 'text.secondary',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {emptyMessage}
      </Box>
    );
  }

  if (mobile) {
    const visibleCols = columns.filter((c) => !c.hideOnMobile);
    return (
      <>
      {selection && selectableKeys.length > 0 && (
        <FormControlLabel
          sx={{ ml: 0, mb: 0.5 }}
          control={<Checkbox size="small" checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />}
          label={`Select all (${selectableKeys.length})`}
        />
      )}
      <Stack
        component="ul"
        role="list"
        aria-label={ariaLabel}
        spacing={1}
        sx={{ listStyle: 'none', p: 0, m: 0 }}
      >
        {rows.map((row, rIdx) => {
          const key = getRowKey(row, rIdx);
          const inner = (
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack spacing={0.75}>
                {visibleCols.map((col) => (
                  <Box
                    key={col.id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '40% 1fr',
                      gap: 1,
                      alignItems: 'baseline',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        fontSize: 11,
                      }}
                    >
                      {col.header}
                    </Typography>
                    <Box
                      sx={{
                        fontSize: 13,
                        textAlign: col.align ?? 'left',
                        wordBreak: 'break-word',
                      }}
                    >
                      {col.cell(row, rIdx)}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          );
          return (
            <Card
              key={key}
              component="li"
              variant="outlined"
              sx={{ borderRadius: 1.5 }}
            >
              {selection && canSelect(row) && (
                <Box sx={{ px: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        size="small"
                        checked={selection.selected.has(key)}
                        onChange={() => toggle(key)}
                        inputProps={{ 'aria-label': selection.label(row) }}
                      />
                    )}
                    label="Select"
                  />
                </Box>
              )}
              {onRowClick ? (
                <CardActionArea
                  onClick={() => onRowClick(row, rIdx)}
                  aria-label={`Open record ${rIdx + 1}`}
                >
                  {inner}
                </CardActionArea>
              ) : (
                inner
              )}
            </Card>
          );
        })}
      </Stack>
      </>
    );
  }

  return (
    <TableContainer component={Box} sx={{ overflowX: 'auto' }}>
      <Table size={size} aria-label={ariaLabel}>
        {caption && (
          <caption className="visually-hidden">{caption}</caption>
        )}
        <TableHead>
          <TableRow>
            {selection && (
              <TableCell padding="checkbox" scope="col">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  disabled={selectableKeys.length === 0}
                  onChange={toggleAll}
                  inputProps={{ 'aria-label': 'Select all' }}
                />
              </TableCell>
            )}
            {columns.map((col) => (
              <TableCell
                key={col.id}
                align={col.align}
                style={col.width ? { width: col.width } : undefined}
                scope="col"
              >
                {col.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rIdx) => (
            <TableRow
              key={getRowKey(row, rIdx)}
              hover={!!onRowClick}
              onClick={onRowClick ? () => onRowClick(row, rIdx) : undefined}
              sx={onRowClick ? { cursor: 'pointer' } : undefined}
            >
              {selection && (
                // Ticking a box does not open the row.
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  {canSelect(row) && (
                    <Checkbox
                      size="small"
                      checked={selection.selected.has(getRowKey(row, rIdx))}
                      onChange={() => toggle(getRowKey(row, rIdx))}
                      inputProps={{ 'aria-label': selection.label(row) }}
                    />
                  )}
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align}>
                  {col.cell(row, rIdx)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ResponsiveTable;
